import { useEffect, useState } from 'react'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { Banner, BannerVariants } from '@/ui'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  getCurrentUser,
  selectIsImpersonating,
} from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'

const BANNER_TEXT =
  'A quick update: we’ve made a few changes to our Terms of Service.'
const BANNER_CTA_TEXT = 'See what’s new'
const BANNER_HREF =
  'https://support.joinheard.com/hc/en-us/articles/44481881307287/'

export const tosUpdateBannerDismissedKey = (userId: number): string =>
  `tosUpdateBannerDismissed:${userId}`

const readDismissed = (userId: number): boolean => {
  try {
    return (
      window.localStorage.getItem(tosUpdateBannerDismissedKey(userId)) ===
      'true'
    )
  } catch {
    return false
  }
}

const writeDismissed = (userId: number): void => {
  try {
    window.localStorage.setItem(tosUpdateBannerDismissedKey(userId), 'true')
  } catch {
    return
  }
}

export const TosUpdateBanner = () => {
  const currentUser = useReselector(getCurrentUser)
  const isImpersonating = useReselector(selectIsImpersonating)
  const flagEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.tosUpdateSignInBanner,
    false
  )
  const track = useAnalyticsTrack()
  const [dismissedUserId, setDismissedUserId] = useState<number | null>(null)

  const visible =
    flagEnabled &&
    currentUser !== null &&
    !currentUser.admin &&
    !isImpersonating &&
    dismissedUserId !== currentUser.id &&
    !readDismissed(currentUser.id)

  useEffect(() => {
    if (visible) {
      track('viewed terms of service update banner')
    }
  }, [track, visible])

  if (!visible) {
    return null
  }

  const onDismiss = () => {
    track('dismissed terms of service update banner')
    writeDismissed(currentUser.id)
    setDismissedUserId(currentUser.id)
  }

  return (
    <Banner
      data-testid="tos-update-banner"
      variant={BannerVariants.ANNOUNCEMENT}
      text={BANNER_TEXT}
      href={BANNER_HREF}
      ctaText={BANNER_CTA_TEXT}
      ctaLinkNewPage
      onDismiss={onDismiss}
    />
  )
}
